/**
 * DonationCapture.tsx - Main Donation Flow Controller Component
 *
 * This component orchestrates the complete donation capture flow with
 * multi-step navigation, validation, and payment processing integration.
 *
 * Key Features:
 * - Multi-step donation flow (Amount → Donor → Payment → Success)
 * - Step navigation with progress indicators
 * - Form validation and error handling
 * - Embedded and modal display modes
 * - Payment processing integration
 * - Success and failure state handling
 *
 * Donation Flow Steps:
 * 1. Amount Selection: Choose donation amount and frequency
 * 2. Donor Information: Collect donor contact details
 * 3. Payment Processing: Handle payment method and processing
 * 4. Success/Failure: Display confirmation or error handling
 *
 * Display Modes:
 * - Embedded: Inline form within fundraiser page
 * - Modal: Popup overlay for focused donation experience
 *
 * Props:
 * - isVisible: boolean - Component visibility state
 * - onClose: () => void - Close handler for modal mode
 * - isEmbedded?: boolean - Whether displayed as embedded form
 *
 * Dependencies: Step components, MobX store, portal rendering
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { X, Heart } from "lucide-react";
import { DonationCaptureAmountStep } from "./DonationCaptureAmountStep";
import { DonationCaptureDonorStep } from "./DonationCaptureDonorStep";
import { DonationCapturePaymentStep } from "./DonationCapturePaymentStep";
import { DonationCaptureSucceeded } from "./DonationCaptureSucceeded";
import { DonationCaptureFailed } from "./DonationCaptureFailed";
import { fundraiserStore } from "../../stores/FundraiserStore";
import {
  DonationAmountSelection,
  DonationPopupModalType,
  FundRaiserStatus,
} from "../../enums";
import {
  CustomerFacingCheckOutItemModel,
  DonationItem,
  DonationPricingPageInputsModel,
  IAPIPaymentReceiptViewModel,
  MultipleItems,
  PurchaseLinkService,
  PurchasePaymentIntentModel,
  PurchaseRoutes,
  PurchaseSection,
  PurchaseStore,
  PurchaseWrapper,
  SingleItemMultiplePrices,
} from "@mindmemobile/mm-payment-modules";
import { CreateFundraiserModel, DonorInfo } from "../../models";
import { DonationCapturePaymentMethodStep } from "./DonationCapturePaymentMethodStep";
import { NMIPaymentMethodFormHandle } from "@mindmemobile/mm-payment-modules/src/shared/components/NMIPaymentMethodForm";
import { set } from "lodash";
import { count } from "console";
import { parseCurrencyAmount } from "../../utils/formatters";

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const DonationCaptureContainer = styled.div<{
  isEmbedded: boolean;
  currentStep: number;
}>`
  background: #ffffff;
  border-radius: ${(props) => (props.isEmbedded ? "16px" : "24px")};
  width: 100%;
  max-width: ${(props) => (props.isEmbedded ? "100%" : "600px")};
  max-height: ${(props) =>
    props.isEmbedded ? "none" : props.currentStep === 1 ? "95vh" : "90vh"};
  overflow-y: ${(props) => (props.isEmbedded ? "visible" : "auto")};
  position: ${(props) => (props.isEmbedded ? "relative" : "relative")};
  box-shadow: ${(props) =>
    props.isEmbedded
      ? "0 4px 20px rgba(0, 0, 0, 0.08)"
      : "0 20px 60px rgba(0, 0, 0, 0.3)"};
  border: ${(props) =>
    props.isEmbedded
      ? "1px solid #f1f5f9"
      : "1px solid rgba(255, 255, 255, 0.2)"};
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button<{ isEmbedded: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: ${(props) => (props.isEmbedded ? "none" : "flex")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Header = styled.div`
  text-align: center;
  padding: 40px 32px 24px;
  border-bottom: 1px solid #f1f5f9;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 63px;
  margin-bottom: 0;
  padding-top: 5px;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

const StepLabel = styled.div<{
  active: boolean;
  completed: boolean;
  clickable: boolean;
}>`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) =>
    props.active ? "#10b981" : props.completed ? "#1e293b" : "#9ca3af"};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: color 0.2s ease;

  &:hover {
    color: ${(props) =>
      props.clickable
        ? props.active
          ? "#10b981"
          : props.completed
            ? "#1e293b"
            : "#9ca3af"
        : "inherit"};
  }
`;

const StepIndicator = styled.div<{ active: boolean; completed: boolean }>`
  width: calc(100% + 8px);
  height: 4px;
  border-radius: 2px;
  background: ${(props) =>
    props.active ? "#10b981" : props.completed ? "#1e293b" : "#e2e8f0"};
  transition: all 0.3s ease;
  margin-top: 2px;
`;

const FormContainer = styled.div<{ currentStep: number; isEmbedded: boolean }>`
  padding: 16px 32px 100px;
  overflow-y: auto;
  flex: 1;
`;

const FooterContainer = styled.div<{ isEmbedded: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #f1f5f9;
  padding: 20px 32px;
  z-index: 10;
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 20px;
  background: #10b981;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #059669;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

interface AmountData {
  selectedAmount: number | null;
  customAmount: string;
  frequency: "one-time" | "monthly";
  comment: string;
}

// interface PaymentInfo {
//   cardNumber: string;
//   expirationDate: string;
//   securityCode: string;
//   accountHolderName: string;
//   accountType: string;
// }

interface DonationCaptureProps {
  isVisible: boolean;
  onClose: () => void;
  isEmbedded?: boolean;
  promoterId?: string;
  shortUniqueId: string;
}

const steps = ["AMOUNT", "DONOR", "PAYMENT"];

export const DonationCapture: React.FC<DonationCaptureProps> = ({
  isVisible,
  onClose,
  promoterId,
  shortUniqueId,
  isEmbedded = false,
}) => {
  const [paymentIntent, setPaymentIntent] = useState<
    PurchasePaymentIntentModel | undefined
  >(undefined);
  const [currentStep, setCurrentStep] = useState(0);
  const [showResult, setShowResult] = useState<"success" | "failed" | null>(
    null
  );
  // The receipt the pay API returns - the same content the confirmation email rendered. The
  // success screen downloads exactly this, so the PDF and the email can never disagree.
  const [receipt, setReceipt] = useState<IAPIPaymentReceiptViewModel | null>(
    null
  );
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    new Set([0])
  );
  const [furthestStep, setFurthestStep] = useState(0);
  const modalScrollableContentRef = useRef<HTMLDivElement>(null);

  // Step data states
  const [amountData, setAmountData] = useState<AmountData>({
    selectedAmount: null,
    customAmount: "",
    frequency: "one-time",
    comment: "",
  });
  const [isPurchaseInProgress, setIsPurchaseInProgress] = useState(false);

  const USCountry = fundraiserStore.countryStore.getUSCountry();

  const initialDonorInfo = new DonorInfo({
    countryDetails: USCountry,
    country: USCountry?.id,
    phoneCountryId: USCountry?.id,
    phoneCountryCode: USCountry?.code?.toString() || "",
    receiveNotifications: true,
  });

  const [donorData, setDonorData] = useState<DonorInfo>(initialDonorInfo);

  // const [paymentData, setPaymentData] = useState<PaymentInfo>({
  //   cardNumber: "",
  //   expirationDate: "",
  //   securityCode: "",
  //   accountHolderName: "",
  //   accountType: "Personal",
  // });

  const [paymentMethod, setPaymentMethod] = useState<"card" | "bank">("card");
  const [paymentMethodToken, setPaymentMethodToken] = useState<string | null>(
    null
  );
  const formRef = useRef<NMIPaymentMethodFormHandle>(null);
  // Step validation states
  const [step1Valid, setStep1Valid] = useState(false);
  const [step2Valid, setStep2Valid] = useState(false);
  const [step3Valid, setStep3Valid] = useState(false);

  const [purchaseStore] = React.useState(
    () => new PurchaseStore(fundraiserStore.countryStore)
  );
  useEffect(() => {
    const usCountry = fundraiserStore.countryStore.getUSCountry();
    if (usCountry) {
      purchaseStore.initPurchaseSectionData(usCountry);
    }
  }, [fundraiserStore.countryStore.countryDataLoaded]);

  useEffect(() => {
    if (
      selectedFundraiser.merchantAccount.isNMIMerchantAccount() &&
      paymentMethodToken
    ) {
      handlePurchase();
    }
  }, [paymentMethodToken]);

  const selectedFundraiser = fundraiserStore.selectedFundraiser;
  // Surcharge: donor opts in by default when the fundraiser asks first.
  const [coverSurcharge, setCoverSurcharge] = useState(true);
  // The actual amount charged, taken from the API response (authoritative - includes surcharge).
  const [amountPaid, setAmountPaid] = useState<number | null>(null);
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isEmbedded) {
      onClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && !isEmbedded) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isVisible && !isEmbedded) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";

      // Reset all values when modal opens
      resetForm();

      // Scroll to top when form loads
      setTimeout(() => {
        if (modalScrollableContentRef.current) {
          modalScrollableContentRef.current.scrollTo({
            top: 0,
            behavior: "instant" as any,
          });
        }
      }, 0);
    } else if (!isEmbedded) {
      document.body.style.overflow = "unset";
    }

    return () => {
      if (!isEmbedded) {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      }
    };
  }, [isVisible, isEmbedded]);

  const resetForm = () => {
    setIsPurchaseInProgress(false);
    setCurrentStep(0);
    setShowResult(null);
    setCompletedSteps(new Set([0]));
    setFurthestStep(0);
    setPaymentMethodToken(null);
    setAmountData({
      selectedAmount: null,
      customAmount: "",
      frequency: "one-time",
      comment: "",
    });

    setDonorData(initialDonorInfo);

    // setPaymentData({
    //   cardNumber: "",
    //   expirationDate: "",
    //   securityCode: "",
    //   accountHolderName: "",
    //   accountType: "Personal",
    // });

    setStep1Valid(false);
    setStep2Valid(false);
    setStep3Valid(false);
  };

  const getCurrentAmount = () => {
    if (amountData.customAmount) {
      return parseCurrencyAmount(amountData.customAmount);
    }
    return amountData.selectedAmount || 0;
  };

  const getSurchargePercentage = () =>
    parseFloat(
      (selectedFundraiser?.surchargePercentage || "0")
        .toString()
        .replace(/,/g, "")
    ) || 0;

  /* A surcharge exists only when it is switched on AND has a rate. A 0% rate - a bad save, or a
     migration that could not resolve the merchant's card rate - has to behave exactly like "off":
     asking the donor to agree to a "0% surcharge" that adds nothing is worse than saying nothing. */
  const hasSurcharge = () =>
    !!selectedFundraiser?.surchargeEnabled && getSurchargePercentage() > 0;

  // Whether the payment step should show the donor a "cover the cost" opt-in checkbox.
  const showSurchargeOption = () =>
    hasSurcharge() && !!selectedFundraiser?.surchargeAskCustomerFirst;

  /* Surcharge applied without asking: the donor gets no checkbox, so the amount has to be stated
     outright or they would see a total larger than the donation they chose with no explanation. */
  const isSurchargeAutoApplied = () =>
    hasSurcharge() && !selectedFundraiser?.surchargeAskCustomerFirst;

  // The surcharge that would apply on the current amount, ignoring the donor's choice.
  const getPotentialSurchargeAmount = () => {
    if (!hasSurcharge()) return 0;
    return Math.round(getCurrentAmount() * getSurchargePercentage()) / 100;
  };

  // The surcharge actually charged: applied automatically unless the fundraiser asks the
  // donor first, in which case it depends on the donor's checkbox.
  const getSurchargeAmount = () => {
    if (!hasSurcharge()) return 0;
    const applies = selectedFundraiser?.surchargeAskCustomerFirst
      ? coverSurcharge
      : true;
    return applies ? getPotentialSurchargeAmount() : 0;
  };

  const getTotalPayable = () => getCurrentAmount() + getSurchargeAmount();

  // Whether the donor is covering the surcharge (the API adds it server-side).
  const donorCoversSurcharge = () => {
    if (!hasSurcharge()) return false;
    return selectedFundraiser?.surchargeAskCustomerFirst ? coverSurcharge : true;
  };

  const formatAmount = (amount: number) => {
    // A surcharge introduces cents (0.6% of $100 is $0.60), and rounding them away would show a
    // total that differs from what is actually charged. Show cents only when there are any, so
    // round donation amounts still read as "$100" rather than "$100.00".
    const hasCents = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const isValidDonorInfo = donorData.validate();
      if (!isValidDonorInfo) {
        setDonorData(new DonorInfo(donorData)); // Trigger re-render to show errors
        return;
      } else {
        setStep2Valid(true);
      }
    }

    if (currentStep < steps.length - 1) {
      // Mark current step as completed and move to next
      setCompletedSteps(
        (prev) => new Set(Array.from(prev).concat(currentStep + 1))
      );

      setCurrentStep(currentStep + 1);
      setFurthestStep(Math.max(furthestStep, currentStep + 1));

      // Scroll to top when moving to next step
      if (modalScrollableContentRef.current) {
        modalScrollableContentRef.current.scrollTo({
          top: 0,
          behavior: "instant" as any,
        });
      }
    }
  };

  const handleStepClick = (stepIndex: number) => {
    // Allow clicking on any completed step or current step
    if (stepIndex <= furthestStep) {
      setCurrentStep(stepIndex);

      // Scroll to top when changing steps
      if (modalScrollableContentRef.current) {
        modalScrollableContentRef.current.scrollTo({
          top: 0,
          behavior: "instant" as any,
        });
      }
    }
  };

  const handlePayment = () => {
    if (
      selectedFundraiser.merchantAccount.isNMIMerchantAccount() &&
      !paymentMethodToken
    ) {
      formRef.current?.validateCardDetails();
      return;
    }

    // handlePurchase re-checks every step itself (it is also reached from the NMI token callback).
    handlePurchase();
  };

  /* Final check before charging, on EVERY route to a purchase. This checked step 1 three times (a
     copy/paste slip), and the NMI card-token callback called handlePurchase directly, so donor details
     and the amount were never re-validated there (security finding F05). The API still enforces
     amount/campaign rules on its side. */
  function canSubmitPurchase(): boolean {
    if (!step1Valid || getCurrentAmount() <= 0) {
      setCurrentStep(0);
      return false;
    }
    if (!donorData.validate()) {
      setDonorData(new DonorInfo(donorData)); // re-render to show the errors
      setCurrentStep(1);
      return false;
    }
    return true;
  }

  function handlePurchase() {
    if (!paymentMethodToken) return;
    if (!canSubmitPurchase()) return;
    setIsPurchaseInProgress(true);
    const request = selectedFundraiser.createPurchaseAPIRequest(
      promoterId || "",
      // Send the base donation amount; the API adds the surcharge server-side when covered.
      getCurrentAmount(),
      amountData.frequency === "monthly",
      paymentMethodToken || "",
      paymentIntent?.paymentIntentId || "",
      donorData,
      amountData.comment,
      donorCoversSurcharge()
    );

    PurchaseLinkService.purchaseLinkCheckoutItems(request).subscribe({
      next: (result) => {
        if (result.result?.success) {
          // Use the amount the API actually charged (donation + surcharge), not local state.
          setAmountPaid(result.result?.amountPaid ?? null);
          setReceipt(result.result?.receipt || null);
          setShowResult("success");
        } else {
          setShowResult("failed");
        }
        setIsPurchaseInProgress(false);
      },
      error: () => {
        setShowResult("failed");
        setIsPurchaseInProgress(false);
      },
    });
  }

  const handleNewPaymentMethod = (resetPaymentIntent = false) => {
    // Reset to payment step and clear payment data
    setCurrentStep(2);
    setShowResult(null);
    // setPaymentData({
    //   cardNumber: "",
    //   expirationDate: "",
    //   securityCode: "",
    //   accountHolderName: "",
    //   accountType: "Personal",
    // });
    //setPaymentMethod("card");
    setStep3Valid(false);

    // Scroll to top when returning to payment step
    if (modalScrollableContentRef.current) {
      modalScrollableContentRef.current.scrollTo({
        top: 0,
        behavior: "instant" as any,
      });
    }
    if (resetPaymentIntent) {
      setPaymentIntent(undefined);
    }
  };

  const handleSuccessClose = () => {
    onClose();
  };

  const getStepValidity = (stepIndex: number) => {
    switch (stepIndex) {
      case 0:
        return step1Valid;
      case 1:
        return step2Valid;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const getButtonText = () => {
    // Check fundraiser status and start date conditions
    if (selectedFundraiser) {
      const isNotActive = selectedFundraiser.status !== FundRaiserStatus.Active;
      const isPending =
        selectedFundraiser.startDate &&
        new Date(selectedFundraiser.startDate) > new Date();

      if (isNotActive) {
        return "Not Accepting New Donations";
      }

      if (isPending) {
        const startDate = new Date(selectedFundraiser.startDate);
        const today = new Date();
        const timeDiff = startDate.getTime() - today.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
        return `Donate in ${daysDiff} Day${daysDiff !== 1 ? "s" : ""}`;
      }
    }

    switch (currentStep) {
      case 0:
        return "Enter Donor Info";
      case 1:
        return "Enter Payment Info";
      case 2:
        return `Pay ${formatAmount(getTotalPayable())}${amountData.frequency === "monthly" ? " Monthly" : ""}`;
      default:
        return "Next";
    }
  };

  const getButtonAction = () => {
    // Check if button should be disabled due to fundraiser status/date
    if (selectedFundraiser) {
      const isNotActive = selectedFundraiser.status !== FundRaiserStatus.Active;
      const isPending =
        selectedFundraiser.startDate &&
        new Date(selectedFundraiser.startDate) > new Date();

      if (isNotActive || isPending) {
        return () => {}; // Disabled action
      }
    }

    switch (currentStep) {
      case 0:
      case 1:
        return handleNext;
      case 2:
        return handlePayment;
      default:
        return handleNext;
    }
  };

  const isButtonDisabled = () => {
    // Check fundraiser status and date conditions first
    if (selectedFundraiser) {
      const isNotActive = selectedFundraiser.status !== FundRaiserStatus.Active;
      const isPending =
        selectedFundraiser.startDate &&
        new Date(selectedFundraiser.startDate) > new Date();

      if (isNotActive || isPending) {
        return true;
      }
    }

    // Then check step validation
    return !getStepValidity(currentStep) || isPurchaseInProgress;
  };

  const renderStepContent = () => {
    // Show result pages if payment was processed
    if (showResult === "success") {
      const donationData = {
        // Prefer the API's charged amount (includes surcharge); fall back to the local total.
        amount: amountPaid ?? getTotalPayable(),
        frequency: amountData.frequency,
        donorInfo: donorData,
        paymentMethod: paymentMethod,
        //paymentInfo: paymentData,
      };

      // Get legal info from builder data
      const legalInfo = {
        legalName: fundraiserStore.builderData?.legalName,
        // email: fundraiserStore.builderData?.email,
        // phone: fundraiserStore.builderData?.phone
      };

      return (
        <DonationCaptureSucceeded
          donationData={donationData}
          onClose={handleSuccessClose}
          onMakeAnotherDonation={() => {
            resetForm();
            setShowResult(null);
          }}
          legalInfo={legalInfo}
          isEmbedded={isEmbedded}
          shortUniqueId={shortUniqueId}
          receipt={receipt}
        />
      );
    }

    if (showResult === "failed") {
      const donationData = {
        amount: getCurrentAmount(),
        frequency: amountData.frequency,
        donorInfo: donorData,
        paymentMethod: paymentMethod,
        //paymentInfo: paymentData,
      };

      return (
        <DonationCaptureFailed
          donationData={donationData}
          onNewPaymentMethod={() => handleNewPaymentMethod(true)}
          isEmbedded={isEmbedded}
        />
      );
    }

    // Show normal step content
    switch (currentStep) {
      case 0:
        return (
          <DonationCaptureAmountStep
            initialData={amountData}
            onDataChange={setAmountData}
            onValidationChange={setStep1Valid}
          />
        );
      case 1:
        return (
          <DonationCaptureDonorStep
            countriesWithState={fundraiserStore.countryStore.sortCountriesByName()}
            initialData={donorData}
            onDataChange={setDonorData}
            onValidationChange={setStep2Valid}
          />
        );
      case 2:
        return (
          <DonationCapturePaymentMethodStep
            paymentIntent={paymentIntent}
            frequency={amountData.frequency}
            selectedFundraiser={fundraiserStore.selectedFundraiser}
            onPaymentMethodTokenUpdate={handlePaymentTokenUpdate}
            amount={parseFloat(
              amountData.selectedAmount?.toString() ||
                amountData.customAmount ||
                "0"
            )}
            ref={formRef}
            accountId={fundraiserStore.selectedFundraiser.accountId}
            merchantAccount={fundraiserStore.selectedFundraiser.merchantAccount}
            onCreatePaymentIntent={(paymentIntent) =>
              setPaymentIntent(paymentIntent)
            }
            showSurchargeOption={showSurchargeOption()}
            surchargeAutoApplied={isSurchargeAutoApplied()}
            surchargeAmount={getPotentialSurchargeAmount()}
            coverSurcharge={coverSurcharge}
            onToggleCoverSurcharge={setCoverSurcharge}
            chargeAmount={getTotalPayable()}
          />
        );
      default:
        return null;
    }
  };

  const purchaseRouteContent = (
    <PurchaseRoutes
      countryStore={fundraiserStore.countryStore}
      purchaseStore={purchaseStore}
      isRenderFromFundraiser={true}
      isPurchaseLink={true}
      customerFacingCheckOutDetails={
        selectedFundraiser.donationCustomerFacingCheckOutDetails
      }
      /* The loaded fundraiser's own id first: selectedFundraiserId is only ever set by the builder
         and the admin details screen, so on the public page it is null - and PurchaseRoutes sends
         it as the request's id, which the API rejects ("CheckOutId" required) before charging
         anything. Falls back to the store for the builder preview. */
      fundraiserId={
        selectedFundraiser?.id || fundraiserStore.selectedFundraiserId || ""
      }
      promoterId={promoterId}
    />
  );

  const embeddedContent = renderStepContent();
  const content = (
    <DonationCaptureContainer isEmbedded={isEmbedded} currentStep={currentStep}>
      <CloseButton isEmbedded={isEmbedded} onClick={onClose}>
        <X size={20} />
      </CloseButton>

      <Header id="donation-capture-header">
        <Title>
          <Heart size={32} color="#10b981" />
          Make a Donation
        </Title>

        <StepsContainer>
          {steps.map((stepName, index) => (
            <Step
              key={stepName}
              active={index === currentStep}
              completed={index < currentStep}
              onClick={() => handleStepClick(index)}
            >
              <StepLabel
                active={index === currentStep}
                completed={index < currentStep}
                clickable={index <= furthestStep}
              >
                {stepName}
              </StepLabel>
              {index === currentStep && (
                <StepIndicator
                  active={index === currentStep}
                  completed={index < currentStep}
                />
              )}
            </Step>
          ))}
        </StepsContainer>
      </Header>

      <FormContainer
        ref={modalScrollableContentRef}
        currentStep={currentStep}
        isEmbedded={isEmbedded}
      >
        {renderStepContent()}
      </FormContainer>

      <FooterContainer isEmbedded={isEmbedded}>
        <ActionButton onClick={getButtonAction()} disabled={isButtonDisabled()}>
          {getButtonText()}
        </ActionButton>
      </FooterContainer>
    </DonationCaptureContainer>
  );

  const popupContent = createPortal(
    <DonationCaptureContainer isEmbedded={false} currentStep={currentStep}>
      <CloseButton isEmbedded={isEmbedded} onClick={onClose}>
        <X size={20} />
      </CloseButton>
      <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
        {fundraiserStore.selectedFundraiser.popupModalType ===
        DonationPopupModalType.DonationItem
          ? purchaseRouteContent
          : content}
      </ModalOverlay>
    </DonationCaptureContainer>,
    document.body
  );

  // For embedded mode, don't render if not visible
  if (isEmbedded && !isVisible) {
    return null;
  }
  // For result pages, render without the normal step structure
  if (showResult) {
    // For embedded mode, return content directly
    if (isEmbedded) {
      return embeddedContent;
    }

    // For modal mode, wrap in portal with overlay
    return popupContent;
  }

  // For embedded mode, return content directly
  if (isEmbedded) {
    return content;
  }

  function handlePaymentTokenUpdate(paymentMethodToken: string) {
    setPaymentMethodToken(paymentMethodToken);
    if (paymentMethodToken) {
      setStep3Valid(true);
    }
  }

  // For modal mode, wrap in portal with overlay
  return popupContent;
};
